/**
 * Operation photos — the product's signature: every operation gets a matching
 * image automatically. v1: a small bundled set with keyword matching on the
 * operation title. v2 (planned): a large R2-hosted bank with a manifest.
 *
 * Matching is deliberately dumb and deterministic: lowercase the title, first
 * keyword group that hits wins, otherwise a neutral default. Titles arrive in
 * many languages (the household's own), so keywords include he/ur/tl where a
 * household is likely to write them.
 */
import airport from './op-photos/airport.jpg';
import bedlinen from './op-photos/bedlinen.jpg';
import carservice from './op-photos/carservice.jpg';
import carwash from './op-photos/carwash.jpg';
import drycleaning from './op-photos/drycleaning.jpg';
import groceries from './op-photos/groceries.jpg';
import guestroom from './op-photos/guestroom.jpg';
import receipt from './op-photos/receipt.jpg';
import school from './op-photos/school.jpg';

const RULES: Array<{ photo: string; keywords: string[] }> = [
  { photo: airport, keywords: ['airport', 'flight', 'dxb', 'terminal', 'שדה', 'טיסה'] },
  { photo: school, keywords: ['school', 'pickup', 'pick up', 'kids', 'בית ספר', 'ילדים'] },
  {
    photo: groceries,
    keywords: [
      'grocery',
      'groceries',
      'supermarket',
      'waitrose',
      'spinneys',
      'market',
      'קניות',
      'סופר',
    ],
  },
  { photo: guestroom, keywords: ['guest', 'room', 'wing', 'bedroom', 'אורחים', 'חדר'] },
  { photo: bedlinen, keywords: ['linen', 'bed', 'sheet', 'מצעים'] },
  { photo: carservice, keywords: ['service', 'registration', 'mechanic', 'טסט', 'רישויון'] },
  { photo: carwash, keywords: ['wash', 'car wash', 'cars', 'ניקוי רכב', 'שטיפ'] },
  { photo: drycleaning, keywords: ['dry clean', 'laundry', 'כביסה'] },
  { photo: receipt, keywords: ['receipt', 'expense', 'supplies', 'locksmith', 'קבלה', 'אספקה'] },
];

const DEFAULTS = [guestroom, groceries, carservice];

/** The receipt photograph, for money cards with no real receipt image of
 *  their own — the design's rotated thumbnail next to the amount. */
export const receiptPhoto: string = receipt;

/** Deterministic photo for an operation title. Never throws, never network. */
export function opPhoto(title: string): string {
  const t = title.toLowerCase();
  for (const rule of RULES) {
    if (rule.keywords.some((k) => t.includes(k))) return rule.photo;
  }
  // Stable default per title — the same task always gets the same photo.
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) | 0;
  return DEFAULTS[Math.abs(h) % DEFAULTS.length] ?? guestroom;
}
